/**
 * @fileoverview DuckDB WASM singleton for Arrow registration and SQL execution.
 *
 * Exports a singleton `DuckDb` instance — import it and call methods directly.
 */

import type * as duckdbWasm from "@duckdb/duckdb-wasm";
import type { DataType, LanesColumn } from "@repo/lanes/types";
import { MAX_RESULT_BYTES } from "@repo/shared/types/dataset-run-status";
import type {
  DataType as ArrowDataTypeClass,
  Table as ArrowTable,
} from "apache-arrow";

import { buildCellConverter } from "~/libs/arrow-cell";
import { logger } from "~/libs/logger";

/** Lazily-loaded DuckDB WASM module — only imported in the browser to avoid
 *  the Node.js CJS entrypoint which tries to require unavailable native modules. */
const loadDuckDBWasm = () => import("@duckdb/duckdb-wasm");
const loadArrow = () => import("apache-arrow");

/** Maps an Apache Arrow field type to the platform's DataType. */
const arrowTypeToDataType = (arrowType: ArrowDataTypeClass): DataType => {
  if (!arrowMod) throw new Error("apache-arrow not loaded yet");
  const { DataType: ArrowDataType } = arrowMod;
  if (
    ArrowDataType.isInt(arrowType) ||
    ArrowDataType.isFloat(arrowType) ||
    ArrowDataType.isDecimal(arrowType)
  )
    return "NUMBER";
  if (ArrowDataType.isBool(arrowType)) return "BOOLEAN";
  if (
    ArrowDataType.isDate(arrowType) ||
    ArrowDataType.isTimestamp(arrowType) ||
    ArrowDataType.isTime(arrowType)
  )
    return "DATE";
  if (
    ArrowDataType.isList(arrowType) ||
    ArrowDataType.isFixedSizeList(arrowType)
  )
    return "ARRAY";
  if (ArrowDataType.isStruct(arrowType) || ArrowDataType.isMap(arrowType))
    return "OBJECT";
  return "STRING";
};

let arrowMod: Awaited<ReturnType<typeof loadArrow>> | null = null;

/**
 * Payloads under this size are registered as buffers rather than file handles:
 * their bytes are needed to detect the empty-run sentinel, and a handle saves
 * nothing at this scale.
 */
const EMPTY_RUN_MAX_BYTES = 1024;

/**
 * Largest cached payload {@link DuckDBWorker.registerRunFile} will fall back to
 * buffering when handle registration fails. Copying the file into the WASM heap
 * is a last resort at any size; past this it is worse than the failure it is
 * recovering from, since wasm32 has ~4 GB of address space in total.
 */
const HANDLE_FALLBACK_MAX_BYTES = MAX_RESULT_BYTES;

/** Builds the `name -> LanesColumn` metadata map from an Arrow result's schema. */
const buildColumns = (table: ArrowTable): Record<string, LanesColumn> =>
  table.schema.fields.reduce<Record<string, LanesColumn>>((acc, f) => {
    acc[f.name] = {
      name: f.name,
      type: arrowTypeToDataType(f.type),
      databaseType: f.type.toString(),
    };
    return acc;
  }, {});

export type DuckDBRowResult = {
  columns: Record<string, LanesColumn>;
  /** The first result row, or `null` when the query returned no rows. */
  row: Record<string, unknown> | null;
};

/**
 * Result of {@link DuckDb.queryTable}: column metadata plus the raw columnar
 * Arrow Table. Unlike {@link DuckDb.queryRow}, no per-row JS objects are
 * materialised — callers read cells lazily (see `rift-data.ts`), keeping the
 * result close to its Arrow wire size instead of a 10-20x row-object blow-up.
 */
export type DuckDBTableResult = {
  columns: Record<string, LanesColumn>;
  table: ArrowTable;
};

/**
 * Thin wrapper around DuckDB-WASM that manages a single persistent instance
 * and provides dataset-oriented registration/query helpers.
 */
export class DuckDb {
  private db: duckdbWasm.AsyncDuckDB | undefined;
  private conn: duckdbWasm.AsyncDuckDBConnection | undefined;
  private initPromise: Promise<void> | null = null;
  private emptyRuns = new Set<string>();

  private getDB(): duckdbWasm.AsyncDuckDB {
    if (!this.db)
      throw new Error("Database not initialized. You must call init() first.");
    return this.db;
  }

  /** Initializes DuckDB-WASM. Safe to call multiple times — deduped to a single promise. */
  async init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.doInit();
    }
    return this.initPromise;
  }

  /** Awaits initialization — called at the start of every public method. */
  private async ready(): Promise<void> {
    await this.init();
  }

  private async doInit(): Promise<void> {
    try {
      const dwasm = await loadDuckDBWasm();
      arrowMod = await loadArrow();

      const DUCKDB_BUNDLES = dwasm.getJsDelivrBundles();
      const bundle = await dwasm.selectBundle(DUCKDB_BUNDLES);

      if (!bundle.mainWorker) {
        throw new Error("Failed to create DuckDB worker");
      }

      const worker = await dwasm.createWorker(bundle.mainWorker);
      const duckLogger = new dwasm.ConsoleLogger(dwasm.LogLevel.ERROR);
      const db = new dwasm.AsyncDuckDB(duckLogger, worker);

      await db.instantiate(bundle.mainModule);

      // Initialize the database and connection
      this.db = db;
      this.conn = await this.db.connect();

      // Load Arrow extension once for Arrow support
      await this.conn.query("INSTALL nanoarrow FROM community;");
      await this.conn.query("LOAD nanoarrow;");
    } catch (error) {
      // Clear the promise so a retry can attempt initialization again
      this.initPromise = null;
      throw new Error("Database initialization failed", { cause: error });
    }
  }

  // ===========================================================================
  // Public methods
  // ===========================================================================

  /**
   * Registers Arrow bytes for a run id under the stable DuckDB filename
   * `{runId}.arrow`. Runs are immutable, so a registered buffer never needs
   * to be overwritten. Detects empty/sentinel files and memoizes them so
   * future queries can short-circuit to an empty result.
   *
   * Prefer {@link registerRunFile} when the payload is already cached in OPFS:
   * this copies the whole payload into the WASM heap.
   */
  async registerRunBuffer(runId: string, bytes: Uint8Array): Promise<void> {
    await this.ready();
    this.emptyRuns.delete(runId);
    const db = this.getDB();

    if (await this.memoizeIfEmpty(runId, bytes)) return;

    await db.registerFileBuffer(`${runId}.arrow`, bytes);
  }

  /**
   * Registers a run's OPFS-cached Arrow file by handle, so DuckDB reads it from
   * disk instead of holding a second copy of the payload in the WASM heap. This
   * is what keeps peak memory proportional to a query's *result* rather than to
   * the size of the source file.
   *
   * Falls back to buffer registration in two cases: tiny payloads (where the
   * bytes are needed anyway to detect the empty-run sentinel, and a handle buys
   * nothing), and a handle registration failure — most likely another tab
   * holding the file's exclusive access lock. The second fallback only applies
   * up to {@link HANDLE_FALLBACK_MAX_BYTES}; above it the original failure is
   * rethrown rather than trying to copy a multi-GB file into the heap.
   */
  async registerRunFile(
    runId: string,
    cached: { handle: FileSystemFileHandle; file: File },
  ): Promise<void> {
    await this.ready();
    this.emptyRuns.delete(runId);
    const db = this.getDB();
    const fileName = `${runId}.arrow`;

    if (cached.file.size < EMPTY_RUN_MAX_BYTES) {
      const bytes = new Uint8Array(await cached.file.arrayBuffer());
      if (await this.memoizeIfEmpty(runId, bytes)) return;
      await db.registerFileBuffer(fileName, bytes);
      return;
    }

    try {
      const dwasm = await loadDuckDBWasm();
      await db.registerFileHandle(
        fileName,
        cached.handle,
        dwasm.DuckDBDataProtocol.BROWSER_FSACCESS,
        true,
      );
    } catch (err) {
      if (cached.file.size > HANDLE_FALLBACK_MAX_BYTES) {
        logger.warn(
          { err, runId, byteLength: cached.file.size },
          "duckdb file handle registration failed and the payload is too large to buffer",
        );
        throw err;
      }
      logger.debug(
        { err, runId },
        "duckdb file handle registration failed, falling back to buffer",
      );
      const bytes = new Uint8Array(await cached.file.arrayBuffer());
      await db.registerFileBuffer(fileName, bytes);
    }
  }

  /**
   * Detects the empty-run sentinel (a zero-column or `_empty`-only Arrow file)
   * and memoizes the run so later queries short-circuit. Returns whether the
   * run was empty.
   */
  private async memoizeIfEmpty(
    runId: string,
    bytes: Uint8Array,
  ): Promise<boolean> {
    if (bytes.byteLength >= EMPTY_RUN_MAX_BYTES) return false;
    try {
      const { tableFromIPC } = arrowMod ?? (await loadArrow());
      const table = tableFromIPC(bytes);
      const hasOnlySentinel = table.schema.fields.every(
        (f) => f.name === "_empty",
      );
      if (table.schema.fields.length === 0 || hasOnlySentinel) {
        this.emptyRuns.add(runId);
        return true;
      }
    } catch {
      // Let DuckDB handle malformed tiny payloads.
    }
    return false;
  }

  /**
   * Executes SQL that is expected to yield a single row (stat/footer aggregates,
   * MIN/MAX ranges) and materialises only that first row. Reads each column
   * element-wise via `Vector.get(0)` so nulls are preserved and temporal columns
   * surface as numbers rather than bigint; `buildCellConverter` additionally
   * converts Decimal/HUGEINT aggregates (e.g. `SUM(...)`) to scale-aware numbers.
   * Returns `row: null` when the result is empty.
   */
  async queryRow(sql: string): Promise<DuckDBRowResult> {
    await this.ready();

    for (const runId of this.emptyRuns) {
      if (sql.includes(`${runId}.arrow`)) {
        return { columns: {}, row: null };
      }
    }

    const db = this.getDB();
    const conn = await db.connect();

    try {
      // duckdb-wasm bundles its own apache-arrow (v17); cast to the app's
      // arrow (v21) Table type. The runtime shape is compatible.
      const result = (await conn.query(sql)) as unknown as ArrowTable;

      const columns = buildColumns(result);
      if (result.numRows === 0) {
        return { columns, row: null };
      }

      const row: Record<string, unknown> = {};
      for (const field of result.schema.fields) {
        const child = result.getChild(field.name);
        if (!child) continue;
        row[field.name] = buildCellConverter(field.type)(child.get(0));
      }

      return { columns, row };
    } catch (err) {
      if (err instanceof Error && err.message.includes("at least one column")) {
        const match = sql.match(/([\da-f-]+)\.arrow/);
        if (match?.[1]) this.emptyRuns.add(match[1]);
        return { columns: {}, row: null };
      }
      logger.error({ err, sql }, "duckdb queryRow failed");
      throw err;
    } finally {
      await conn.close();
    }
  }

  /**
   * Executes SQL and returns the raw columnar Arrow Table plus column metadata,
   * without materialising per-row JS objects. Callers wrap the table in a lazy
   * `RiftData` handle and read cells on demand, keeping memory close to the
   * Arrow wire size. Use this for large result sets (table rifts); `queryRow()`
   * handles single-row stat/footer aggregates.
   */
  async queryTable(sql: string): Promise<DuckDBTableResult> {
    await this.ready();

    for (const runId of this.emptyRuns) {
      if (sql.includes(`${runId}.arrow`)) {
        return { columns: {}, table: emptyArrowTable() };
      }
    }

    const db = this.getDB();
    const conn = await db.connect();

    try {
      // duckdb-wasm bundles its own apache-arrow (v17); cast to the app's
      // arrow (v21) Table type. The runtime shape is compatible.
      const table = (await conn.query(sql)) as unknown as ArrowTable;
      return { columns: buildColumns(table), table };
    } catch (err) {
      if (err instanceof Error && err.message.includes("at least one column")) {
        const match = sql.match(/([\da-f-]+)\.arrow/);
        if (match?.[1]) this.emptyRuns.add(match[1]);
        return { columns: {}, table: emptyArrowTable() };
      }
      logger.error({ err, sql }, "duckdb queryTable failed");
      throw err;
    } finally {
      await conn.close();
    }
  }
}

/** Constructs an empty Arrow Table (zero rows, zero columns) for empty runs. */
const emptyArrowTable = (): ArrowTable => {
  if (!arrowMod) throw new Error("apache-arrow not loaded yet");
  return arrowMod.tableFromArrays({});
};

const duckdb = new DuckDb();

// Kick off initialization eagerly in the browser so callers that arrive
// before the route's `beforeLoad` simply await the in-flight promise.
// Guarded because TanStack Start loads modules server-side even with ssr: false.
if (typeof window !== "undefined") {
  duckdb.init();
}

export default duckdb;
